import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Grid
} from '@mui/material';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { UpdatePaymentInfoInput } from 'generated/graphql';
import {
  fields,
  PaymentCountrySelectField,
  USStateSelectField
} from 'modules';
import { useForm } from 'react-hook-form';

import * as dataService from '../dataService';



const ControlledTextField = fields.genTextFieldControllerComponent<dataService.IFormData>();
const ControlledStripeElementField = fields.genStripeElementField<dataService.IFormData>();
const PaymentCountrySelectFieldController = fields.genEnumSelectFieldControllerComponent<dataService.IFormData, string>(PaymentCountrySelectField);
const USStateSelectFieldController = fields.genEnumSelectFieldControllerComponent<dataService.IFormData, string>(USStateSelectField({
  label: 'State'
}));


interface ICreditCardProps {
  defaultValues: dataService.IData;
  onUpdate: (args: UpdatePaymentInfoInput) => void;
  loading: boolean
}

export const CreditCardForm: React.FC<ICreditCardProps> = props => {
  const form = useForm<dataService.IFormData>({
    resolver: yupResolver(dataService.__formSchema),
    shouldFocusError: true
  });
  const stripe = useStripe();
  const elements = useElements();

  React.useEffect(() => {
    form.reset({
      country: props.defaultValues.country,
      usState: props.defaultValues.usState,
      city: props.defaultValues.city,
      address: props.defaultValues.address,
      zipCode: props.defaultValues.zipCode
    });
  }, [form, props.defaultValues]);



  async function processSubmit(data: dataService.IFormData) {
    if (!stripe || !elements) {
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    const cardExpiryElement = elements.getElement(CardExpiryElement);
    const cardCvcElement = elements.getElement(CardCvcElement);

    if (cardNumberElement && cardExpiryElement && cardCvcElement) {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
          address: {
            postal_code: data.zipCode
          }
        }
      });

      if (paymentMethod && props.onUpdate) {
        await props.onUpdate({
          paymentId: paymentMethod.id,
          country: data.country,
          usState: data.usState,
          city: data.city,
          address: data.address,
          zipCode: data.zipCode
        });
      }

      if (error) {
        console.log('[error]', error);
      } else {
        console.log('[PaymentMethod]', paymentMethod);
      }

      form.reset();
    }
  }



  return (
    <form onSubmit={form.handleSubmit(processSubmit)}>
      <Grid container spacing={5}>
        <Grid
          container item
          spacing={5}>
          <Grid item xs={4}>
            <ControlledStripeElementField
              formControl={form.control}
              label="Card Number"
              fieldKey="cardNumber"
              backgroundColor='white'/>
          </Grid>

          <Grid item xs={4}>
            <ControlledStripeElementField
              formControl={form.control}
              label="Exp. Date"
              fieldKey="expirationDate"
              backgroundColor='white'/>
          </Grid>

          <Grid item xs={4}>
            <ControlledStripeElementField
              formControl={form.control}
              label="CVV"
              fieldKey="cvv"
              backgroundColor='white'/>
          </Grid>

          <Grid item xs>
            <PaymentCountrySelectFieldController
              fieldKey="country"
              formControl={form.control}
            />
          </Grid>

          <Grid item xs>
            <USStateSelectFieldController
              formControl={form.control}
              fieldKey="usState" />
          </Grid>

          <Grid item xs>
            <ControlledTextField
              formControl={form.control}
              label="City"
              fieldKey="city" />
          </Grid>

          <Grid item xs>
            <ControlledTextField
              formControl={form.control}
              label="Address"
              fieldKey="address" />
          </Grid>

          <Grid item xs>
            <ControlledTextField
              formControl={form.control}
              fieldKey="zipCode"
              label="Zip Code" />
          </Grid>
        </Grid>

        <Grid item xs>
          <Button
            type="submit"
            variant="contained"
            size="medium"
            disabled={props.loading}>
          Update Payment Info
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};
